import type { IDiagnosticReportGetOne } from '../../interfaces/diagnosticReportGetOne';
import type { IProcess, IProcessSupervision } from '../../interfaces/process';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import type { ISupervisionGetOne } from '../../interfaces/supervisionGetOne';
import type { IItemState } from '../episodes/Episode';
import { CustomControlEntity } from '../../../../common/enums/customControlEntity';
import { ProcessScrollItem } from '../../../../common/enums/processScrollItem';
import { SupervisionStatus } from '../../../../common/enums/supervisionStatus';
import { fetchJson } from '../../../store/fetch';
import { Loading } from '../../elements/common';
import { Component } from '../../elements/Component';
import { loadCustomControl } from '../customControls/loadCustomControl';
import { ProcessContainer, ProcessItemScrollElement } from '../episodes/common';
import { SupervisionItemEdit } from './SupervisionItemEdit';

interface ISupervisionItemProps {
    formId: string;
    isFirstItem: boolean;
    isLastItem: boolean;
    isPreviousRequest: boolean;
    itemState: IItemState;
    mode?: SupervisionMode;
    onLoaded: () => void;
    process: IProcess;
    reloadProcess: () => Promise<void>;
    supervision: IProcessSupervision;
    updateItemState: (item?: IItemState) => Promise<void>;
}

export interface INewSupervision {
    diagnosticReportId: string;
    gradingSummary: null;
    id: undefined;
    patientId: string;
    performerOrganizationId: string;
    performerPractitionerId: string;
    requestId: string;
    status: SupervisionStatus;
}

interface ISupervisionItemState {
    diagnosticReport?: IDiagnosticReportGetOne;
    request?: IRequestGetOne;
    supervision?: ISupervisionGetOne | INewSupervision;
}

export enum SupervisionMode {
    showBiometrics = 'biometrics',
    showGrading = 'grading',
}

export class SupervisionItem extends Component<ISupervisionItemProps, ISupervisionItemState> {
    public async componentDidMount() {
        if (this.props.itemState.mode === 'edit') {
            await this.load();
        } else {
            await this.loadSupervision();
            await this.loadDiagnosticReport();
            await this.loadRequest();
        }

        this.props.onLoaded();
    }

    public async componentDidUpdate(prevProps: Readonly<ISupervisionItemProps>) {
        if (
            prevProps.supervision?.status !== this.props.supervision?.status ||
            (!this.props.itemState.initialIsOpen && prevProps.itemState.initialIsOpen)
        ) {
            await this.load();
        }
    }

    public render() {
        return (
            <>
                <ProcessItemScrollElement
                    item={ProcessScrollItem.supervision}
                    requestId={this.props.process.id}
                />
                <ProcessContainer>
                    <Loading
                        item={this.state.supervision}
                        loaded={(supervision) => (
                            <>
                                {this.props.itemState.mode === 'edit' && (
                                    <SupervisionItemEdit
                                        formId={this.props.formId}
                                        isFirstItem={this.props.isFirstItem}
                                        isLastItem={this.props.isLastItem}
                                        onCancel={async () => {
                                            supervision.id
                                                ? await this.props.updateItemState({
                                                      ...this.props.itemState,
                                                      mode: 'read',
                                                  })
                                                : await this.props.updateItemState();
                                        }}
                                        onSave={async (id: string) => {
                                            await this.load(id);
                                            await this.props.updateItemState({ ...this.props.itemState, mode: 'read' });
                                            await this.props.reloadProcess();
                                        }}
                                        process={this.props.process}
                                        supervision={supervision}
                                    />
                                )}
                                {this.props.itemState.mode === 'read' && this.props.mode && (
                                    <>{this.renderCustomControls(supervision as ISupervisionGetOne)}</>
                                )}
                            </>
                        )}
                    />
                </ProcessContainer>
            </>
        );
    }

    private renderCustomControls(supervision: ISupervisionGetOne) {
        if (!this.state.diagnosticReport || !this.state.request) {
            return null;
        }

        const CustomControl = loadCustomControl(
            this.props.process.customControlVersion,
            CustomControlEntity.SupervisionRead,
            this.props.process.code,
            this.props.process.serviceType
        );

        return (
            <CustomControl
                acceptDiagnosticReport={async () => {
                    await this.props.reloadProcess();
                    await this.loadDiagnosticReport();
                    await this.loadRequest();
                }}
                diagnosticReport={this.state.diagnosticReport}
                isFirstItem={this.props.isFirstItem}
                isLastItem={this.props.isLastItem}
                isPreviousRequest={this.props.isPreviousRequest}
                itemState={this.props.itemState}
                mode={this.props.mode!}
                process={this.props.process}
                reloadProcess={async () => {
                    await this.load();
                    await this.loadDiagnosticReport();
                    await this.props.reloadProcess();
                }}
                removeSupervision={async () => await this.props.reloadProcess()}
                request={this.state.request}
                serviceCode={this.props.process.code}
                supervision={supervision}
                updateItemState={this.props.updateItemState}
            />
        );
    }

    private async load(id?: string) {
        await this.loadSupervision(id);
    }

    private async loadRequest() {
        const url = `/api/requests/${this.props.process.id}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ request: result.json });
    }

    private async loadSupervision(id?: string) {
        if (this.props.supervision?.id) {
            const url = `/api/supervisions/${this.props.supervision.id}?includeItemAuthorizations=canUpdateSupervision,canTakeSupervision,canDelete`;
            const result = await fetchJson(url, 'GET');
            await this.setState({ supervision: result.json });
        } else if (id) {
            const url = `/api/supervisions/${id}?includeItemAuthorizations=canUpdateSupervision,canTakeSupervision,canDelete`;
            const result = await fetchJson(url, 'GET');
            await this.setState({ supervision: result.json });
        } else {
            await this.createNewSupervision();
        }
    }

    private async loadDiagnosticReport() {
        const url =
            `/api/diagnosticReports/${this.props.process.diagnosticReports[0].id}?&includeItemAuthorizations=` +
            'canUpdateDiagnosticReportToClosed,' +
            'canTakeProcess';

        const result = await fetchJson(url, 'GET');
        await this.setState({ diagnosticReport: result.json });
    }

    private async createNewSupervision() {
        if (this.props.process.task && this.props.process.task.performerPractitionerId) {
            const supervision = {
                id: undefined,
                diagnosticReportId: this.props.process.diagnosticReports[0].id,
                gradingSummary: null,
                patientId: this.props.process.patientId,
                performerOrganizationId: this.props.process.task.performerOrganizationId!,
                performerPractitionerId: this.props.process.task.performerPractitionerId,
                requestId: this.props.process.id,
                status: SupervisionStatus.draft,
            };
            await this.setState({ supervision });
        }
    }
}
